import { create } from 'zustand';
import type { Currency } from '@matix/types';
import { FALLBACK_CURRENCY } from '@matix/types';
import { safePrecision } from '@matix/utils';

/**
 * Tenant base-currency store.
 *
 * Why this exists next to `useCurrency()` (which reads the same value out of
 * the React Query cache): the cart store and the order-request builder are
 * plain modules, not components, and they need the currency's PRECISION to
 * round money. A KWD tenant has 3 decimals; rounding at a hardcoded 2 turned a
 * 0.136 line into 0.14, and the receipt then printed "0.140" for an item
 * priced 0.136.
 *
 * Filled from the PrintConfig query (`usePrintConfig`) — no extra round-trip.
 * Until it resolves, the fallback is EGP/2-decimal, matching the server's own
 * `FALLBACK_CURRENCY`.
 */
interface CurrencyState {
  currency: Currency;
  loaded: boolean;
  /** Decimal places to round money at. Shorthand for `currency.precision`. */
  precision: () => number;
  setCurrency: (c: Currency) => void;
}

export const useCurrencyStore = create<CurrencyState>((set, get) => ({
  currency: FALLBACK_CURRENCY,
  loaded: false,
  precision: () => get().currency.precision,
  setCurrency: (currency) => set({ currency, loaded: true }),
}));

/**
 * Read the money precision from outside React (cart math, wire builder).
 * Always returns a usable number — 2 until the print config lands.
 */
export function moneyPrecision(): number {
  return useCurrencyStore.getState().currency.precision;
}

/**
 * The "effectively zero" threshold for money comparisons: one smallest unit of
 * the tenant's currency, capped at a cent.
 *
 * 0.01 on EGP/SAR/AED (unchanged from the hardcoded value it replaced), 0.001
 * on KWD. Comparing a 3-decimal currency against a whole cent hid real money:
 * a 0.008 KWD balance read as "fully paid".
 */
export function moneyEpsilon(): number {
  return Math.min(0.01, 10 ** -safePrecision(moneyPrecision()));
}

/**
 * Format an amount for a numeric <input> value (plain, unlocalised, at the
 * tenant's precision) — `toFixed(2)` prefilled 0.14 into the payment box for a
 * 0.136 KWD order and the till collected the extra fils.
 */
export function amountText(n: number): string {
  return (Number.isFinite(n) ? n : 0).toFixed(safePrecision(moneyPrecision()));
}

/** `step` for a money <input> — 0.01 on 2-decimal currencies, 0.001 on KWD. */
export function amountStep(): string {
  return (10 ** -safePrecision(moneyPrecision())).toFixed(safePrecision(moneyPrecision()));
}
